import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '@Components/ui/confirm-dialog/confirm-dialog.component';
import { SelectItemComponent } from '@Components/ui/select-item/select-item.component';
import { CurrencyPipe } from '@Components/util/currency.pipe';
import { ENUM_ACTIONS , PeriodState} from '@Interfaces/index';
import { AdfiGraphqlService } from '@services/adfi-graphql.service';
import { AdfiGrowlService } from '@services/adfi-growl.service';
import { AdfiService } from '@services/adfi-service';
import { LoadingService } from '@services/loading.service';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Component({
  selector: 'app-assets-entry-form',
  templateUrl: './assets-entry-form.component.html',
  styleUrls: ['./assets-entry-form.component.scss']
})
export class AssetsEntryFormComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Input() origin: string;
  @Input() item: any;
  @Input() maxValue: number;
  isLoading = true;
  formAssets: FormGroup;
  listTypesProduct: any[];
  listTypesProductFiltered: any[];
  listMarks: any[];
  listMarksFiltered: any[];
  minDate: Date;
  maxDate: Date;
  value: number;
  block : boolean;
  origins = [
    { key: 'COMODATO', value: 'Comodato' },
    { key: 'DONACION', value: 'Donación' },
    { key: 'DOTACION', value: 'Dotación' },
    { key: 'REPOSICION', value: 'Reposición' },
    { key: 'TRASPASO', value: 'Traspaso' }
  ];

  states = [
    { key: 'BUENO', value: 'Bueno' },
    { key: 'REGULAR', value: 'Regular' },
    { key: 'MALO', value: 'Malo' },
  ];

  async getPrefixOptions() {
    return new Promise<void>((resolve, reject) => {
      this.graphqlService.getEntity(
        'assetPrefixes',
        'prefix',
        200,
        '',
        {
          next: (prefixes: any[]) => {
            this.prefixOptions = prefixes
              .map(item => item.prefix);
            this.prefixOptionsByProduct = [...this.prefixOptions];
            this.prefixOptionsFiltered = [...this.prefixOptions];
            resolve();
          },
          error: (error) => {
            this.adfiGrowlService.errorMessage(error, 'Error al cargar los prefijos');
            reject(error);
          }
        },
        true
      );
    });
  }

  prefixOptions: string[] = [];
  prefixOptionsFiltered: string[] = [];
  prefixOptionsByProduct: string[] = [];

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private graphqlService: AdfiGraphqlService,
    public sLoading: LoadingService,
    private adfiGrowlService: AdfiGrowlService,
    private gaService: GoogleAnalyticsService,
    private service: AdfiService
  ) {
    this.formAssets = formBuilder.group({
      origin: ['', Validators.required,],
      typeProduct: ['', Validators.required,],
      prefixDescription: ['', [Validators.required, this.prefixOptionValidator.bind(this)]],
      description: ['', [Validators.required, Validators.maxLength(700)]],
      acquisitionDate: ['', Validators.required,],
      value: ['', [Validators.required, Validators.min(1)]],
      withMark: [''],
      mark: ['', [Validators.required,  this.objectValidator]],
      withSerial: [''],
      serialNumber: ['', [Validators.required, Validators.maxLength(70), Validators.minLength(6)]],
      quantity: ['', [Validators.required, Validators.min(1)]],
      state: ['', Validators.required],
      originalAsset: ['', Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.sLoading.show();
    const period = this.service.periods.find(p => p.estado === "A");
    this.minDate = new Date(period.year, 0, 1);
    this.maxDate = this.item.endDate ? new Date(this.item.endDate) : new Date();
    await this.getTypeProduct();
    await this.getMark();
    await this.getPrefixOptions();
    this.configForm();
    this.block = this.service.validPermission('BlockingPlatform', 'lock', PeriodState.NO_STATUS);
    if (this.origin) {
      this.formAssets.get('origin').setValue(this.origins.find(o => o.key === this.origin));
    }
  }

  save(form: FormGroupDirective) {
    const content = undefined;
    const warnings = [{ fieldName: '', warning: 'Recuerde que, es importante el cargue del ACTA FIRMADA para dar continuidad y afectación al proceso contable, con el cumplimiento de este requisito el proceso quedará en firme.' }];
    const data: ConfirmDialogData = {
      title: '¿Desea guardar la información?',
      content,
      action: ENUM_ACTIONS.CREATE,
      warnings
    };
    const dialog = this.dialog.open(ConfirmDialogComponent, {
      data
    });
    dialog.componentInstance.dialogRef.afterClosed().subscribe(async (asset) => {
      if (asset) {
        const quantity = this.formAssets.get('quantity').enabled ? Number(this.formAssets.get('quantity').value) : 1;

        if (quantity > 1 && this.formAssets.get('typeProduct')?.value && this.formAssets.get('typeProduct')?.value.account._id !== 511114 && !this.formAssets.get('withSerial').value) {
          this.adfiGrowlService.error('Error', 'Recuerde que no se permite el uso de números de serial duplicados. Los elementos deben registrarse individualmente.');
          return;
        }
        this.sLoading.show();

        const next = (data) => {
          this.adfiGrowlService.success('GUARDADO', 'El registro se ha agregado correctamente');
          this.gaService.event('Guardar', 'ADFI', 'Agregar Bienes', this.service.user.id);
          this.close.emit();
          this.sLoading.hide();
        };
        const error = (err) => {
          this.sLoading.hide();
          this.adfiGrowlService.errorMessage(err);
        };

        const isDotacion = form.control.get('origin').value?.key === 'DOTACION';
        const prefix = (isDotacion && form.control.get('prefixDescription').value) ? form.control.get('prefixDescription').value : '';
        const descriptionValue = form.control.get('description').value ? form.control.get('description').value : '';
        const finalDescription = prefix ? `${prefix} - ${descriptionValue}` : descriptionValue;

        this.graphqlService.createEntity('ActivoFijo',
          {
            origen: form.control.get('origin').value.key,
            typeProduct: form.control.get('typeProduct').value ? form.control.get('typeProduct').value.id : null,
            descripcion: finalDescription,
            typeDocument: this.item.typeDocument,
            nroFactura: this.item.documentNumber,
            fechaFactura: form.control.get('acquisitionDate').value,
            fechaFinal: this.origin === 'COMODATO' ? this.maxDate : null,
            valorCompra: this.value,
            third: this.item.third.id,
            cantidad: quantity,
            noSerial: form.control.get('withSerial').enabled ? form.control.get('withSerial').value ? form.control.get('withSerial').value : false : false,
            nroSerial: form.control.get('withSerial').value ? "NO APLICA" : form.control.get('serialNumber').value,
            thirdCheck: form.control.get('withMark').enabled ? form.control.get('withMark').value ? form.control.get('withMark').value : false : false,
            trademarkRef: form.control.get('withMark').value ? null : form.control.get('mark').value.id,
            estadoV: form.control.get('state').value.key,
            base64File: [],
            parentActivoFijo: form.control.get('originalAsset').value ? form.control.get('originalAsset').value.id : null,
            ppyeIncome: this.item.id
          }, undefined, { next, error });
      }
    });
  }
  async cancel() {
    this.close.emit();
  }
  fireCurrency(value: number) {
    this.value = value ? value : 0;
    if(this.value > this.maxValue) this.formAssets.get('value').setErrors({ exceedsAvailable: true });
    if(this.value == this.maxValue && this.formAssets.get('quantity').enabled) this.formAssets.get('quantity').disable();
  }

  preventSubmit(event: KeyboardEvent): void {
    event.preventDefault();
  }

  async getTypeProduct() {
    const filter = 'isEnabled: true';
    const max = await this.graphqlService.countEntity("typeProducts", filter);
    const next = (data) => {
      this.listTypesProduct = data;
      this.listTypesProductFiltered = this.listTypesProduct;
    };
    const error = (e) => {
      this.adfiGrowlService.errorMessage(e, 'Error al cargar los tipos de artículos');
    };
    this.graphqlService.getEntity("typeProducts", "accountUnspsc{description} account{_id} withMark withSerial", max, filter, { next, error });
  }

  async getMark() {
    const max = await this.graphqlService.countEntity("trademarks", '');
    const next = (data) => {
      this.listMarks = data;
      this.listMarksFiltered = this.listMarks;
    };
    const error = (e) => {
      this.adfiGrowlService.errorMessage(e, 'Error al cargar las marcas');
    };
    const aux = this.graphqlService.getEntity("trademarks", "trademarkName", max, '', { next, error });
  }

  private configForm() {

    const setControlsState = (controls: string[]) => {
      controls.forEach(key => {
        if (this.formAssets.get(key)) {
          this.formAssets.get(key).disable();
        }
      });
    };

    this.formAssets.get('origin').valueChanges.subscribe((value) => {
      if (value) {
        switch (value.key) {
          case 'COMODATO':
            setControlsState(['quantity', 'originalAsset', 'prefixDescription']);
            this.listTypesProduct = this.listTypesProduct.filter(value => value.account._id !== 511114);
            break;
          case 'DONACION':
          case 'REPOSICION':
            setControlsState(['originalAsset', 'prefixDescription']);
            break;
          case 'DOTACION':
            setControlsState(['originalAsset']);
            break;
          case 'RECUPERACION':
            setControlsState([
              'prefixDescription', 'description', 'value', 'quantity', 'withMark', 'mark', 'withSerial', 'serialNumber', 'state'
            ]);
            this.formAssets.get('state').setValue(this.states[0]);
            break;
          case 'TRASPASO':
            setControlsState(['originalAsset', 'prefixDescription']);
            break;
        }
        this.formAssets.get('origin').disable({ emitEvent: false });
      }

    });

    this.formAssets.get('typeProduct').valueChanges.subscribe((value) => {
       if (typeof(value) !== 'object') {
        this.listTypesProductFiltered = this.listTypesProduct.filter(item => item.accountUnspsc.description.toLowerCase().includes(value.toLowerCase()));
      }else if (value) {
        value.withMark ? this.formAssets.get('withMark').disable() : this.formAssets.get('withMark').enable();
        value.withSerial ? this.formAssets.get('withSerial').disable() : this.formAssets.get('withSerial').enable();
        this.formAssets.get('quantity').disable();

        const description = value.accountUnspsc.description.toLowerCase();

        this.prefixOptionsByProduct = this.prefixOptions;

        this.prefixOptionsByProduct = [...new Set(this.prefixOptionsByProduct)];
        this.prefixOptionsFiltered = [...this.prefixOptionsByProduct];
        
        if (this.formAssets.get('prefixDescription').value && !this.prefixOptionsByProduct.includes(this.formAssets.get('prefixDescription').value)) {
          this.formAssets.get('prefixDescription').setValue('');
        }
      }
    });

    this.formAssets.get('prefixDescription').valueChanges.subscribe((value) => {
      if (typeof(value) !== 'string') {
        this.prefixOptionsFiltered = [...this.prefixOptionsByProduct];
        return;
      }

      const term = value.toLowerCase().trim();
      this.prefixOptionsFiltered = this.prefixOptionsByProduct.filter(opt => opt.toLowerCase().includes(term));
    });

    this.formAssets.get('withMark').valueChanges.subscribe((value) => {
      value === true ? this.formAssets.get('mark').disable() : this.formAssets.get('mark').enable();
    });
    this.formAssets.get('withSerial').valueChanges.subscribe((value) => {
       if(value === true){
        this.formAssets.get('serialNumber').disable();
        if(this.value < this.maxValue) this.formAssets.get('quantity').enable();
      } else{
        this.formAssets.get('serialNumber').enable();
        if(this.value < this.maxValue) this.formAssets.get('quantity').disable();
      } 
    });

    this.formAssets.get('mark').valueChanges.subscribe(val => {
      if (typeof(val) !== 'object') {
        this.listMarksFiltered = this.listMarks.filter(item => item.trademarkName.toLowerCase().includes(val.toLowerCase()));
      }
    });

    this.isLoading = false;
    this.sLoading.hide();
  }

  removeItem(path: string) {
    this.formAssets.get(path).reset();
  }

  selectAssets() {
    this.formAssets.get('originalAsset').setValue([]);
    let dialogRef: MatDialogRef<any>;
    const data = {
      module: "MovimientoBienes",
      entityName: "ActivoFijo",
      enableAdd: false,
      defaultFilters: [
        {
          column: "cencos",
          value: ""
        },
        {
          column: "dateDown",
          value: true,
          filter: 'existsFilter'
        },
        {
          column: "isActive",
          value: true
        },
        {
          column: "inventoryType",
          value: [0, 1]
        }
      ],
      customTitle: 'Seleccionar Original',
      multiSelect: true,
      displayGroup: 'GS'
    };

    dialogRef = this.dialog.open(SelectItemComponent, {
      data,
      width: "80%",
      maxHeight: "70vh",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.length) {
        result = result.filter((value, index, self) => index === self.findIndex((obj) => obj.id === value.id));
        if (Array.isArray(result)) {
          result.forEach(value => {
            console.log(value);
            value.toString = () => {
              return `${value.consecutive} - ${value.descripcion} - ${value.estadoV}- ${new CurrencyPipe().transform(value.valorCompra)}`;
            }
            this.formAssets.get('originalAsset').setValue(value);
          });
        }
      }
    });
  }

  onButtonClick() {
    this.close.emit();
  }

  displayFn(option: any): string {
    return option ? option.__typename === 'TypeProduct' ? option.accountUnspsc.description : option.trademarkName : '';
  }

  objectValidator(control: AbstractControl) {
    const value = control.value;

    if (!value || typeof value !== 'object') {
      return { notObject: true };
    }

    return null;
  }

  prefixOptionValidator(control: AbstractControl) {
    const value = control.value;

    if (!value) {
      return null;
    }

    if (typeof(value) !== 'string') {
      return { invalidOption: true };
    }

    return this.prefixOptionsByProduct.includes(value) ? null : { invalidOption: true };
  }

  calculateTotal() {
    return (this.value * this.formAssets.get('quantity').value).toFixed(2);  
  }

}
